import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Card, Form, Button, Row, Col, Table, Badge, Alert, Spinner } from 'react-bootstrap';
import { FaLayerGroup, FaPlus, FaTrash, FaEdit, FaSave, FaExclamationTriangle, FaTimes, FaCog } from 'react-icons/fa';
import aiModelApi from '../../services/aiModelApi';
import * as api from '../../services/api';

const LevelManager = ({ setError, setSuccess }) => {
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [levels, setLevels] = useState([]);
  const [allModels, setAllModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form State
  const [formData, setFormData] = useState({
    llm_level: 1,
    llm_key: '',
    category: '',
    sub_category: '',
    agent_utilization: ''
  });

  useEffect(() => {
    fetchAgents();
    fetchAllModels();
  }, []);

  useEffect(() => {
    // Handle blur effect when modal is open
    if (showModal) {
      document.body.classList.add('modal-blur-active');
    } else {
      document.body.classList.remove('modal-blur-active');
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-blur-active');
    };
  }, [showModal]);

  const fetchAgents = async () => {
    try {
      const response = await api.getAgents();
      let agentsList = [];
      if (response && response.agents) {
        agentsList = response.agents;
      } else if (response && response.body && response.body.agents) {
        agentsList = response.body.agents;
      }

      setAgents(agentsList);
      
      // Auto-select first agent if none selected
      if (agentsList.length > 0 && !selectedAgentId) {
        setSelectedAgentId(agentsList[0].uniqueIdentifier);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      setError('Failed to load agents list: ' + error.message);
    }
  };

  const fetchAllModels = async () => {
    try {
      const response = await aiModelApi.levelManager.getAllModels();
      // Handle response structure from API - supporting new data.models structure
      if (response && response.success) {
        const modelsData = response.data?.models || response.models || [];
        setAllModels(modelsData);
      }
    } catch (error) {
      console.error('Error fetching models:', error);
    }
  };

  useEffect(() => {
    if (selectedAgentId) {
      fetchAgentLevels(selectedAgentId);
    }
  }, [selectedAgentId]);

  const fetchAgentLevels = async (agentId) => {
    try {
      setLoading(true);
      const response = await aiModelApi.levelManager.getModelsByAgent(agentId);
      
      if (response && response.success) {
        // Support response.data.models (new structure from instruction) and legacy response.levels
        const rawLevels = response.data?.models || response.levels || [];
        
        // Sort levels by llm_level
        const sortedLevels = rawLevels.sort((a, b) => a.llm_level - b.llm_level);
        setLevels(sortedLevels);
      } else {
        setLevels([]);
      }
    } catch (error) {
      console.error('Error fetching levels:', error);
      setLevels([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (level = null) => {
    if (level) {
      setEditingLevel(level);
      setFormData({
        llm_level: level.llm_level,
        llm_key: level.llm_key,
        category: level.category || '',
        sub_category: level.sub_category || '',
        agent_utilization: level.agent_utilization || ''
      });
    } else {
      setEditingLevel(null);
      // Suggest next level
      const maxLevel = levels.length > 0 ? Math.max(...levels.map(l => l.llm_level)) : 0;
      setFormData({
        llm_level: maxLevel + 1,
        llm_key: allModels.length > 0 ? allModels[0].llm_key : '',
        category: 'general',
        sub_category: 'standard',
        agent_utilization: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingLevel(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const payload = {
        agent_id: parseInt(selectedAgentId),
        llm_level: parseInt(formData.llm_level),
        llm_key: parseInt(formData.llm_key),
        category: formData.category,
        sub_category: formData.sub_category,
        agent_utilization: formData.agent_utilization
      };

      const response = await aiModelApi.levelManager.updateLevel(payload);
      
      if (response && response.success) {
        setSuccess(editingLevel ? 'Level updated successfully' : 'Level created successfully');
        setShowModal(false);
        fetchAgentLevels(selectedAgentId);
      } else {
        setError(response?.error?.message || response?.error || 'Failed to update level');
      }
    } catch (error) {
      console.error('Error saving level:', error);
      setError('Error saving level configuration: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (level) => {
    if (window.confirm(`Are you sure you want to delete Level ${level.llm_level}?`)) {
      try {
        const response = await aiModelApi.levelManager.deleteLevel(selectedAgentId, level.llm_level);
        if (response && response.success) {
          setSuccess('Level deleted successfully');
          fetchAgentLevels(selectedAgentId);
        } else {
          setError(response?.error?.message || response?.error || 'Failed to delete level');
        }
      } catch (error) {
        console.error('Error deleting level:', error);
        setError('Error deleting level: ' + error.message);
      }
    }
  };

  const getSelectedAgentName = () => {
    const agent = agents.find(a => a.uniqueIdentifier === selectedAgentId);
    return agent ? (agent.agentName || agent.repoName) : 'Unknown Agent';
  };

  const getModelInfo = (llmKey) => {
    const model = allModels.find(m => m.llm_key === llmKey);
    return model ? { name: model.model_name, provider: model.provider, cost: model.cost_input_tokens } : null;
  };

  // ENHANCED: Full-Screen Modal Component using React Portal
  // This ensures the modal is rendered at the body level, avoiding z-index issues
  const renderFullScreenModal = () => {
    if (!showModal) return null;

    return createPortal(
      <div className="fullscreen-modal-overlay">
        <div className="fullscreen-modal-container">
          <div className="fullscreen-modal-header">
            <div className="modal-title-section">
              <div className="d-flex align-items-center">
                <FaLayerGroup className="me-3 text-primary" size={32} />
                <div>
                  <h2 className="mb-0">
                    {editingLevel ? `Edit Level ${editingLevel.llm_level}` : 'Add New Level'}
                  </h2>
                  <p className="text-muted mb-0 mt-2">
                    {editingLevel 
                      ? `Modifying routing configuration for Level ${editingLevel.llm_level}` 
                      : `Configure LLM routing level for ${getSelectedAgentName()}`
                    }
                  </p>
                </div>
              </div>
            </div>
            <button 
              className="close-modal-btn"
              onClick={handleCloseModal}
              disabled={submitting}
              type="button"
            >
              <FaTimes size={24} />
            </button>
          </div>

          <div className="fullscreen-modal-body">
            <form onSubmit={handleSubmit} className="modal-form-container">
              {/* Level Configuration Section */}
              <div className="form-section">
                <h4 className="section-title">
                  <FaLayerGroup />
                  Level Configuration
                </h4>
                <Row>
                  <Col xl={4} lg={6} md={12}>
                    <Form.Group className="mb-4">
                      <Form.Label className="required-field">Level Number</Form.Label>
                      <Form.Control 
                        type="number" 
                        name="llm_level" 
                        value={formData.llm_level} 
                        onChange={handleChange}
                        required
                        min="1"
                        size="lg"
                      />
                      <Form.Text className="text-muted">
                        Lower numbers = higher priority/cost models
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col xl={8} lg={6} md={12}>
                    <Form.Group className="mb-4">
                      <Form.Label className="required-field">AI Model</Form.Label>
                      <Form.Select 
                        name="llm_key" 
                        value={formData.llm_key} 
                        onChange={handleChange}
                        required
                        size="lg"
                      >
                        <option value="">Select Model...</option>
                        {allModels.map(model => (
                          <option key={model.llm_key} value={model.llm_key}>
                            {model.model_name} ({model.provider}) - {model.type}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Choose the AI model that will handle requests at this level
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Categorization Section */}
              <div className="form-section">
                <h4 className="section-title">
                  <FaCog />
                  Routing Categories
                </h4>
                <Row>
                  <Col xl={6} lg={6} md={12}>
                    <Form.Group className="mb-4">
                      <Form.Label>Primary Category</Form.Label>
                      <Form.Control 
                        type="text" 
                        name="category" 
                        value={formData.category} 
                        onChange={handleChange}
                        placeholder="e.g. reasoning, general, simple"
                        size="lg"
                      />
                      <Form.Text className="text-muted">
                        Primary classification for routing decisions
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col xl={6} lg={6} md={12}>
                    <Form.Group className="mb-4">
                      <Form.Label>Sub Category</Form.Label>
                      <Form.Control 
                        type="text" 
                        name="sub_category" 
                        value={formData.sub_category} 
                        onChange={handleChange}
                        placeholder="e.g. complex, standard, fast"
                        size="lg"
                      />
                      <Form.Text className="text-muted">
                        Secondary classification for fine-tuned routing
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Utilization Guidelines Section */}
              <div className="form-section">
                <h4 className="section-title">
                  <FaExclamationTriangle />
                  Utilization Guidelines
                </h4>
                <Form.Group className="mb-4">
                  <Form.Label>Agent Utilization Description</Form.Label>
                  <Form.Control 
                    as="textarea" 
                    rows={8}
                    name="agent_utilization" 
                    value={formData.agent_utilization} 
                    onChange={handleChange}
                    placeholder="Describe when the agent should use this level (e.g., 'Complex reasoning tasks requiring step-by-step analysis and detailed explanations')"
                    className="utilization-textarea"
                  />
                  <Form.Text className="text-muted">
                    This helps the agent decide when to route to this model level. Be specific about the types of tasks, complexity, and performance requirements.
                  </Form.Text>
                </Form.Group>
              </div>

              {/* Model Information Display */}
              {formData.llm_key && (
                <div className="form-section">
                  <h4 className="section-title">
                    <FaLayerGroup />
                    Selected Model Information
                  </h4>
                  <div className="model-info-display">
                    {(() => {
                      const modelInfo = getModelInfo(parseInt(formData.llm_key));
                      return modelInfo ? (
                        <div className="p-4 border rounded bg-dark">
                          <Row>
                            <Col xl={4} lg={4} md={12}>
                              <div className="info-item">
                                <strong>Model:</strong> {modelInfo.name}
                              </div>
                            </Col>
                            <Col xl={4} lg={4} md={12}>
                              <div className="info-item">
                                <strong>Provider:</strong> {modelInfo.provider}
                              </div>
                            </Col>
                            <Col xl={4} lg={4} md={12}>
                              <div className="info-item">
                                <strong>Cost:</strong> ${modelInfo.cost}/1K tokens
                              </div>
                            </Col>
                          </Row>
                        </div>
                      ) : (
                        <Alert variant="warning">
                          Model information not available for the selected model.
                        </Alert>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="modal-form-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleCloseModal}
                  disabled={submitting}
                >
                  <FaTimes className="me-2" />
                  Cancel
                </button>
                <button
                  type="submit"
                  className="save-btn"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Spinner size="sm" animation="border" className="me-2" />
                      Saving Level Configuration...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-2" />
                      {editingLevel ? 'Update Level' : 'Create Level'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="level-manager">
      {/* Enhanced Horizontal Agent Selection */}
      <div className="dashboard-filters-container mb-4">
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '2fr 1fr', 
          gap: 'var(--spacing-md)', 
          alignItems: 'end' 
        }}>
          <div className="filter-group">
            <label className="filter-label">Select Agent for Level Management</label>
            <Form.Select 
              value={selectedAgentId} 
              onChange={(e) => setSelectedAgentId(e.target.value)}
              className="filter-control"
              size="lg"
            >
              <option value="">Select Agent...</option>
              {agents.map(agent => (
                <option key={agent.uniqueIdentifier} value={agent.uniqueIdentifier}>
                  {agent.agentName || agent.repoName}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="filter-group">
            <Button 
              variant="primary" 
              onClick={() => handleOpenModal()} 
              disabled={!selectedAgentId}
              className="w-100"
              size="lg"
            >
              <FaPlus className="me-2" />
              Add New Level
            </Button>
          </div>
        </div>
      </div>

      <Card className="ai-card">
        <div className="ai-card-header">
          <h3 className="ai-card-title">
            <FaLayerGroup />
            LLM Routing Levels
          </h3>
          {selectedAgentId && (
            <Badge bg="info" className="ai-badge">
              Agent: {getSelectedAgentName()}
            </Badge>
          )}
        </div>
        <div className="ai-card-body">
          {!selectedAgentId ? (
            <Alert variant="info" className="text-center">
              <FaExclamationTriangle className="me-2" />
              Please select an agent to manage its LLM routing levels.
            </Alert>
          ) : loading ? (
            <div className="text-center p-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading levels for {getSelectedAgentName()}...</p>
            </div>
          ) : (
            <>
              {levels.length > 0 && (
                <div className="mb-3">
                  <Alert variant="info">
                    <strong>Agent:</strong> {getSelectedAgentName()} has {levels.length} level{levels.length !== 1 ? 's' : ''} configured
                  </Alert>
                </div>
              )}
              
              <div className="table-responsive">
                <Table className="ai-table" hover>
                  <thead>
                    <tr>
                      <th>Level</th>
                      <th>Category</th>
                      <th>Model</th>
                      <th>Utilization</th>
                      <th>Cost (Per 1M)</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {levels.length > 0 ? (
                      levels.map(level => (
                        <tr key={level.llm_level_id || level.llm_level}>
                          <td>
                            <Badge bg="primary" className="ai-badge fs-6">
                              Level {level.llm_level}
                            </Badge>
                          </td>
                          <td>
                            <div className="text-white fw-bold">{level.category || 'General'}</div>
                            <small className="text-muted">{level.sub_category || 'Standard'}</small>
                          </td>
                          <td>
                            <div className="fw-bold text-white">{level.model_name || 'Unknown Model'}</div>
                            <div className="small text-muted">{level.provider || 'Unknown Provider'}</div>
                          </td>
                          <td style={{ maxWidth: '300px' }}>
                            <small className="text-muted">
                              {level.agent_utilization || 'No utilization description provided'}
                            </small>
                          </td>
                          <td>
                            <div className="small cost-display">
                              <div>${level.cost_input_tokens || 0}/1M In</div>
                              <div>${level.cost_output_tokens || 0}/1M Out</div>
                            </div>
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button 
                                variant="outline-info" 
                                size="sm" 
                                onClick={() => handleOpenModal(level)}
                                title="Edit level configuration"
                              >
                                <FaEdit />
                              </Button>
                              <Button 
                                variant="outline-danger" 
                                size="sm" 
                                onClick={() => handleDelete(level)}
                                title="Delete level"
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center p-5">
                          <div className="text-muted">
                            <FaLayerGroup size={48} className="mb-3 opacity-50" />
                            <p>No levels configured for {getSelectedAgentName()}.</p>
                            <p>Create your first level to define LLM routing behavior.</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </div>
      </Card>

      {/* Enhanced Full-Screen Modal */}
      {renderFullScreenModal()}
    </div>
  );
};

export default LevelManager;